import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient, withCsrfHeader } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Loader2, Phone, PhoneCall, Check, Plus, Trash2, Lock, Unlock,
  Info, RefreshCw, Download, Settings2, ChevronRight,
  Clock, Filter, X, Voicemail, Mic, MicOff, Play, Square,
  PhoneIncoming, PhoneOutgoing, User, Clock3, FileText, AlertTriangle,
} from "lucide-react";

// ── Plan presentation (copy only) ──────────────────────────────────────────
// Labels + qualitative feature bullets. All dollar amounts and included
// minute/SMS quotas are config-driven and come from /api/admin/dialer/plans
// (the Super Admin Tier Config panel), never hardcoded here.
const PLAN_PRESENTATION: Record<string, { label: string; badge: string; features: string[] }> = {
  lite: {
    label: "Starter", badge: "Free plan",
    features: ["Local number", "Browser dialer"],
  },
  pro: {
    label: "Pro", badge: "Pro plan",
    features: ["Local or toll-free", "Voicemail drop", "IVR routing"],
  },
  business: {
    label: "Pro", badge: "Business plan",
    features: ["Local or toll-free", "Voicemail drop", "IVR routing"],
  },
  enterprise: {
    label: "Enterprise", badge: "Your plan",
    features: ["1 line included free", "IVR + analytics", "Priority support"],
  },
  enterprise_plus: {
    label: "Enterprise+", badge: "Your plan",
    features: ["1 line included free", "IVR + analytics", "Priority support"],
  },
};

// Overage fallback used only until the config-driven plan loads.
const FALLBACK_OVERAGE_MIN_CENTS = 3; // $0.03/min
const FALLBACK_OVERAGE_SMS_CENTS = 2; // $0.02/msg

interface PlanConfig {
  phoneAllowed: boolean;
  dialerIncluded: boolean;
  monthlyFlatCents: number;
  includedMinutes: number;
  includedSms: number;
  overagePerMinuteCents: number;
  overagePerSmsCents: number;
  pricePerAdditionalUserCents: number;
}

interface MergedPlan {
  label: string;
  badge: string;
  features: string[];
  mainCents: number;
  userCents: number;
  mins: number;
  sms: number;
  overageMinCents: number;
  overageSmsCents: number;
}

// Merge presentation copy with the config-driven pricing for a tier.
function mergePlan(tier: string, plansData?: Record<string, PlanConfig>): MergedPlan {
  const pres = PLAN_PRESENTATION[tier] || PLAN_PRESENTATION.lite;
  const cfg = plansData?.[tier];
  return {
    ...pres,
    mainCents: cfg?.monthlyFlatCents ?? 0,
    userCents: cfg?.pricePerAdditionalUserCents ?? 0,
    mins: cfg?.includedMinutes ?? 0,
    sms: cfg?.includedSms ?? 0,
    overageMinCents: cfg?.overagePerMinuteCents ?? FALLBACK_OVERAGE_MIN_CENTS,
    overageSmsCents: cfg?.overagePerSmsCents ?? FALLBACK_OVERAGE_SMS_CENTS,
  };
}

type Tab = "overview" | "voicemail" | "configure" | "usage" | "calls";

function centsToDisplay(cents: number): string {
  if (cents === 0) return "Included";
  return `$${(cents / 100).toFixed(0)}/mo`;
}

function Initials({ name }: { name: string }) {
  const parts = name.trim().split(" ");
  const i = parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
  return (
    <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center text-[10px] font-semibold text-primary flex-shrink-0 font-mono">
      {i}
    </div>
  );
}

// ── Section label ──────────────────────────────────────────────────────────
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] font-medium text-muted-foreground tracking-widest uppercase mb-3">
      {children}
    </div>
  );
}

// ── Thin card ──────────────────────────────────────────────────────────────
function ThinCard({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-card border border-border/60 rounded-lg p-5 ${className}`}>
      {children}
    </div>
  );
}

// ── Step badge ─────────────────────────────────────────────────────────────
function StepBadge({ n, done }: { n: number; done?: boolean }) {
  return (
    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0 ${
      done ? "bg-primary/15 text-primary" : "bg-primary text-primary-foreground"
    }`}>
      {done ? <Check className="w-3 h-3" /> : n}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// OVERVIEW TAB
// ══════════════════════════════════════════════════════════════════════════
interface OverviewData {
  tier: string;
  phoneNumber: string | null;
  phoneProvisioned: boolean;
  callsThisMonth: number;
  minutesThisMonth: number;
  smsThisMonth: number;
  activeRepsThisMonth: number;
  numbersOwned: number;
  billingMonth: string;
  reps: Array<{ userId: string; name: string; initials: string; phoneNumber: string | null; callsThisMonth: number; minutesThisMonth: number; usagePct: number }>;
}

function OverviewTab({ onGoToNumbers }: { onGoToNumbers: () => void }) {
  const { data, isLoading } = useQuery<OverviewData>({ queryKey: ["/api/admin/dialer/overview"] });
  const { data: plansData } = useQuery<{ plans: Record<string, PlanConfig> }>({ queryKey: ["/api/admin/dialer/plans"] });
  const plan = mergePlan(data?.tier || "lite", plansData?.plans);

  if (isLoading) {
    return <div className="flex items-center gap-2 text-sm text-muted-foreground py-8"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  }

  const billingLabel = data?.billingMonth
    ? (() => {
        const [y, m] = data.billingMonth.split("-").map(Number);
        const d = new Date(y, m - 1, 1);
        return d.toLocaleString("default", { month: "long", year: "numeric" });
      })()
    : "";

  return (
    <div className="space-y-5">
      <SectionLabel>Current plan</SectionLabel>
      <ThinCard>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Phone className="w-4 h-4 text-primary" />
            </div>
            <div>
              <div className="text-sm font-medium">{plan.label} workspace dialer</div>
              <div className="text-xs text-muted-foreground">
                {data?.phoneNumber ? `${data.phoneNumber} · ` : ""}
                {plan.mins} min · {plan.sms} SMS · IVR routing
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-0 text-[11px] gap-1">
              <Check className="w-3 h-3" /> Active
            </Badge>
            <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="button-dialer-configure" onClick={onGoToNumbers}>
              Configure
            </Button>
          </div>
        </div>

        <div className="mt-4 h-px bg-border/60" />

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Calls this month", value: (data?.callsThisMonth ?? 0).toLocaleString(), sub: billingLabel },
            { label: "Total minutes", value: (data?.minutesThisMonth ?? 0).toLocaleString(), sub: `of ${plan.mins} included` },
            { label: "Active reps", value: String(data?.activeRepsThisMonth ?? 0), sub: "this month" },
            { label: "Numbers owned", value: String(data?.numbersOwned ?? 0), sub: "workspace + reps" },
          ].map(({ label, value, sub }) => (
            <div key={label} className="bg-muted/40 rounded-md p-3">
              <div className="text-[11px] text-muted-foreground mb-1">{label}</div>
              <div className="text-xl font-semibold font-mono">{value}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>
            </div>
          ))}
        </div>
      </ThinCard>

      <SectionLabel>Rep numbers</SectionLabel>
      <ThinCard className="p-0">
        <div className="divide-y divide-border/50">
          {(data?.reps ?? []).map((rep) => (
            <div key={rep.userId} className="flex items-center gap-3 px-5 py-3" data-testid={`row-rep-overview-${rep.userId}`}>
              <Initials name={rep.name} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{rep.name}</div>
              </div>
              <div className="text-xs font-mono text-muted-foreground w-36 text-right hidden sm:block">
                {rep.phoneNumber || "—"}
              </div>
              <div className="w-20 h-1 bg-muted rounded-full overflow-hidden hidden md:block">
                <div className="h-1 bg-primary rounded-full" style={{ width: `${rep.usagePct}%` }} />
              </div>
              <div className="text-xs text-muted-foreground font-mono w-16 text-right">{rep.callsThisMonth} calls</div>
              <Badge variant={rep.phoneNumber ? "secondary" : "outline"} className={`text-[10px] ${!rep.phoneNumber ? "text-amber-700 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-400 border-amber-200 dark:border-amber-800" : ""}`}>
                {rep.phoneNumber ? "Active" : "No number"}
              </Badge>
            </div>
          ))}
          {(!data?.reps || data.reps.length === 0) && (
            <div className="px-5 py-8 text-sm text-muted-foreground text-center">No reps found in this workspace.</div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-border/50">
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={onGoToNumbers} data-testid="button-add-rep-number">
            <Plus className="w-3.5 h-3.5" /> Add number for rep
          </Button>
        </div>
      </ThinCard>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// CONFIGURE TAB
// ══════════════════════════════════════════════════════════════════════════

interface IvrConfig {
  routingMode: string;
  greetingText: string | null;
  menuItems: Array<{ key: string; label: string; action: string; userId?: string; userIds?: string[]; forwardNumber?: string }>;
  ringTimeoutSeconds: number;
  missedCallAction: string;
  voicemailGreetingText: string | null;
  hasVoicemailGreetingAudio: boolean;
  fallbackForwardNumber: string | null;
}

interface DialerUser {
  userId: string;
  name: string;
  email: string;
  callerIdOverride: string | null;
  callerIdLocked: boolean;
  bridgePhone: string | null;
  routingPreference?: string;
}

// Routing reality for one rep on an INBOUND call (Task #612). Inbound always
// dials a saved personal phone alongside the browser client, so the only
// "won't ring" case left is a rep with no number on file — they are
// browser-only and unreachable when no tab is open.
function inboundPhoneStatus(user: DialerUser | undefined) {
  if (!user) return { willRing: false, label: "No rep selected", detail: "This option rings nobody. Pick a rep." };
  const phone = (user.bridgePhone || "").trim();
  if (phone) {
    return { willRing: true, label: phone, detail: `Rings ${phone} alongside their browser.` };
  }
  return {
    willRing: false,
    label: "Browser only",
    detail: "No personal phone on file — this rep only rings if they have the app open in a tab.",
  };
}

// Small inline badge showing whether a rep's personal phone will be dialed.
function PhoneRoutingBadge({ user, testId }: { user: DialerUser | undefined; testId: string }) {
  const status = inboundPhoneStatus(user);
  return (
    <span
      title={status.detail}
      className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${
        status.willRing
          ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
          : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
      }`}
      data-testid={testId}
    >
      {status.willRing ? <Check className="h-2.5 w-2.5" /> : <AlertTriangle className="h-2.5 w-2.5" />}
      {status.willRing ? <span className="font-mono">{status.label}</span> : status.label}
    </span>
  );
}

// Editor-local shape for one "Press N for …" option (Task #613). Every field
// is always present (empty string / empty array when unused) so the row editor
// can switch actions without losing what the admin already typed.
interface MenuItemDraft {
  key: string;
  label: string;
  action: string;              // ring_user | ring_group | external_forward | voicemail
  userId: string;              // ring_user
  userIds: string[];           // ring_group
  forwardNumber: string;       // external_forward (also usable as overflow)
}

const MENU_ACTION_OPTIONS = [
  { value: "ring_user", label: "Ring rep" },
  { value: "ring_group", label: "Ring group" },
  { value: "external_forward", label: "Forward to number" },
  { value: "voicemail", label: "Voicemail" },
];

// Multi-select rep picker for a "ring group" menu option. Rendered as a
// popover-free inline checklist inside a Select-styled trigger so it matches
// the density of the rest of the row.
function GroupRepPicker({
  users, selected, onChange, testId,
}: {
  users: DialerUser[];
  selected: string[];
  onChange: (next: string[]) => void;
  testId: string;
}) {
  const [open, setOpen] = useState(false);
  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };
  const summary = selected.length === 0
    ? "Pick reps"
    : selected.length === 1
      ? (users.find((u) => u.userId === selected[0])?.name || "1 rep")
      : `${selected.length} reps`;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="h-7 w-36 px-2 text-xs flex items-center justify-between rounded-md border border-input bg-background hover:bg-accent/50 transition-colors"
        data-testid={testId}
      >
        <span className={`truncate ${selected.length ? "" : "text-muted-foreground"}`}>{summary}</span>
        <ChevronRight className={`h-3 w-3 flex-shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-1 w-56 max-h-56 overflow-y-auto rounded-md border border-border bg-popover shadow-md p-1" data-testid={`${testId}-menu`}>
            {users.length === 0 && <p className="px-2 py-1.5 text-xs text-muted-foreground">No reps in this workspace.</p>}
            {users.map((u) => {
              const checked = selected.includes(u.userId);
              return (
                <button
                  key={u.userId}
                  type="button"
                  onClick={() => toggle(u.userId)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-accent text-left"
                  data-testid={`${testId}-option-${u.userId}`}
                >
                  <span className={`h-3.5 w-3.5 flex-shrink-0 rounded border flex items-center justify-center ${checked ? "bg-primary border-primary" : "border-muted-foreground/40"}`}>
                    {checked && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                  </span>
                  <span className="truncate flex-1">{u.name || u.email}</span>
                  {!((u.bridgePhone || "").trim()) && (
                    <AlertTriangle className="h-3 w-3 text-amber-500 flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

interface WorkspaceDialerSettings {
  dialerRecordingMode: string;
  dialerRecordingModeLocked: boolean;
}

interface PhoneStatusData {
  provisioned: boolean;
  status?: string;
  phoneNumber?: string;
  subaccountSid?: string;
  provisionedAt?: string;
  billing?: { monthlyFlatCents: number; includedMinutes: number; includedSms: number; overagePerMinuteCents: number; overagePerSmsCents: number } | null;
  usage?: { billingMonth: string; minutesUsed: number; smsUsed: number; billedAt: string | null } | null;
}

const ROUTING_OPTIONS = [
  { value: "menu", label: "Keypress menu", desc: "Caller presses a digit to reach a department — a group of reps, an outside number, one rep, or voicemail" },
  { value: "round_robin", label: "Round-robin", desc: "One rep at a time, rotated per call" },
  { value: "ring_all", label: "Ring all", desc: "Every rep's browser and bridge phone ring simultaneously" },
  { value: "auto_attendant", label: "Auto-attendant", desc: "Play greeting then ring all reps" },
];

const RECORDING_MODE_LABELS: Record<string, string> = {
  off: "Off — never record",
  all: "Always record — every call",
  opt_out: "On by default — rep can press Stop mid-call",
  opt_in: "Off by default — rep must press Record mid-call",
};

function ConfigureTab({ onGoToNumbers }: { onGoToNumbers: () => void }) {
  const { toast } = useToast();

  const { data: phoneData } = useQuery<PhoneStatusData>({ queryKey: ["/api/admin/phone"] });
  const { data: ivrData, isLoading: ivrLoading } = useQuery<IvrConfig>({ queryKey: ["/api/admin/dialer/ivr"] });
  const { data: settingsData } = useQuery<WorkspaceDialerSettings>({ queryKey: ["/api/admin/dialer/settings"] });
  const { data: users = [] } = useQuery<DialerUser[]>({ queryKey: ["/api/admin/dialer/users"] });

  const [routingMode, setRoutingMode] = useState("ring_all");
  const [greetingText, setGreetingText] = useState("");
  const [ringTimeout, setRingTimeout] = useState("30");
  const [missedCallAction, setMissedCallAction] = useState("voicemail");
  const [voicemailGreeting, setVoicemailGreeting] = useState("");
  const [fallbackForward, setFallbackForward] = useState("");
  const [menuItems, setMenuItems] = useState<MenuItemDraft[]>([]);
  const [recordingMode, setRecordingMode] = useState("off");
  const [smartRecording, setSmartRecording] = useState(false);
  const [recordingLocked, setRecordingLocked] = useState(false);
  const [backfillDateFrom, setBackfillDateFrom] = useState("");
  const [backfillDateTo, setBackfillDateTo] = useState("");
  const [backfillResult, setBackfillResult] = useState<{ total: number; matched: number; updated: number } | null>(null);

  // Voicemail greeting audio recording state
  const [hasAudioGreeting, setHasAudioGreeting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!ivrData) return;
    setRoutingMode(ivrData.routingMode || "ring_all");
    setGreetingText(ivrData.greetingText || "");
    setRingTimeout(String(ivrData.ringTimeoutSeconds || 30));
    setMissedCallAction(ivrData.missedCallAction || "voicemail");
    setVoicemailGreeting(ivrData.voicemailGreetingText || "");
    setFallbackForward(ivrData.fallbackForwardNumber || "");
    setMenuItems((ivrData.menuItems || []).map((i) => ({
      key: i.key,
      label: i.label,
      action: i.action,
      userId: i.userId || "",
      userIds: Array.isArray(i.userIds) ? i.userIds : [],
      forwardNumber: i.forwardNumber || "",
    })));
    setHasAudioGreeting(!!(ivrData.hasVoicemailGreetingAudio));
  }, [ivrData]);

  useEffect(() => {
    if (!settingsData) return;
    setRecordingMode(settingsData.dialerRecordingMode || "off");
    setSmartRecording(false);
    setRecordingLocked(!!settingsData.dialerRecordingModeLocked);
  }, [settingsData]);

  const saveIvr = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PUT", "/api/admin/dialer/ivr", {
        routingMode,
        greetingText: greetingText.trim() || null,
        ringTimeoutSeconds: parseInt(ringTimeout, 10) || 30,
        missedCallAction,
        voicemailGreetingText: voicemailGreeting.trim() || null,
        fallbackForwardNumber: fallbackForward.trim() || null,
        // Only send the fields the chosen action actually uses — the server
        // validates per-action and stores the normalised shape.
        menuItems: routingMode === "menu"
          ? menuItems
              .filter((i) => i.key && i.label.trim())
              .map((i) => ({
                key: i.key,
                label: i.label.trim(),
                action: i.action,
                ...(i.action === "ring_user" ? { userId: i.userId } : {}),
                ...(i.action === "ring_group" ? { userIds: i.userIds } : {}),
                ...(i.forwardNumber.trim() ? { forwardNumber: i.forwardNumber.trim() } : {}),
              }))
          : [],
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Configuration saved" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/dialer/ivr"] });
    },
    onError: (e: any) => toast({ title: "Save failed", description: String(e?.message || e), variant: "destructive" }),
  });

  const saveRecording = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PATCH", "/api/admin/dialer/settings", {
        dialerRecordingMode: recordingMode,
        dialerRecordingModeLocked: recordingLocked,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Recording settings saved" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/dialer/settings"] });
    },
    onError: (e: any) => toast({ title: "Save failed", description: String(e?.message || e), variant: "destructive" }),
  });

  const backfillRecordings = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/dialer/backfill-recordings", {
        ...(backfillDateFrom ? { dateFrom: backfillDateFrom } : {}),
        ...(backfillDateTo   ? { dateTo:   backfillDateTo   } : {}),
      });
      return res.json();
    },
    onSuccess: (data) => {
      setBackfillResult(data);
      queryClient.invalidateQueries({ queryKey: ["/api/dialer/calls"] });
      toast({ title: "Backfill complete", description: `${data.updated} recording${data.updated !== 1 ? "s" : ""} saved (${data.matched} call${data.matched !== 1 ? "s" : ""} matched of ${data.total} Twilio recordings scanned)` });
    },
    onError: (e: any) => toast({ title: "Backfill failed", description: String(e?.message || e), variant: "destructive" }),
  });

  // ── Greeting audio recording helpers ─────────────────────────────────────
  const uploadAudioGreeting = useMutation({
    mutationFn: async (blob: Blob) => {
      const reader = new FileReader();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const res = await apiRequest("POST", "/api/admin/dialer/ivr/greeting-audio", { audioDataUrl: dataUrl });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Greeting saved", description: "Callers will hear your recorded greeting before the voicemail beep." });
      setHasAudioGreeting(true);
      setRecordedBlob(null);
      setRecordedUrl(null);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/dialer/ivr"] });
    },
    onError: (e: any) => toast({ title: "Upload failed", description: String(e?.message || e), variant: "destructive" }),
  });

  const deleteAudioGreeting = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", "/api/admin/dialer/ivr/greeting-audio", undefined);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Greeting removed", description: "Callers will hear the TTS text greeting (if set)." });
      setHasAudioGreeting(false);
      setRecordedBlob(null);
      setRecordedUrl(null);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/dialer/ivr"] });
    },
    onError: (e: any) => toast({ title: "Delete failed", description: String(e?.message || e), variant: "destructive" }),
  });

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      // Prefer formats Twilio can play
      const preferredMime = ["audio/mp4", "audio/ogg;codecs=opus", "audio/webm;codecs=opus", ""]
        .find((m) => !m || MediaRecorder.isTypeSupported(m)) ?? "";
      const mr = new MediaRecorder(stream, preferredMime ? { mimeType: preferredMime } : undefined);
      mediaRecorderRef.current = mr;
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        setRecordedBlob(blob);
        setRecordedUrl(URL.createObjectURL(blob));
        setIsRecording(false);
        if (timerRef.current) clearInterval(timerRef.current);
      };
      mr.start(250);
      setIsRecording(true);
      setRecordingSeconds(0);
      timerRef.current = setInterval(() => setRecordingSeconds((s) => s + 1), 1000);
    } catch (err: any) {
      toast({ title: "Microphone access denied", description: "Allow microphone access in your browser to record.", variant: "destructive" });
    }
  }, [toast]);

  const stopRecording = useCallback(() => {
    mediaRecorderRef.current?.stop();
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  const addMenuItem = () => {
    const used = new Set(menuItems.map((i) => i.key));
    const next = ["1","2","3","4","5","6","7","8","9"].find((k) => !used.has(k)) || "";
    setMenuItems((prev) => [...prev, { key: next, label: "", action: "ring_user", userId: "", userIds: [], forwardNumber: "" }]);
  };

  return (
    <div className="space-y-5">
      {/* Workspace number */}
      <SectionLabel>Workspace number</SectionLabel>
      <ThinCard>
        {phoneData?.provisioned && phoneData.status === "active" ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
                <Phone className="w-4 h-4 text-muted-foreground" />
              </div>
              <div>
                <div className="text-sm font-semibold font-mono">{phoneData.phoneNumber}</div>
                <div className="text-xs text-muted-foreground">Workspace shared number · provisioned by Clarik</div>
              </div>
            </div>
            <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-0 text-[11px] gap-1">
              <Check className="w-3 h-3" /> Active
            </Badge>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-muted-foreground">No platform-provisioned number yet.</div>
            <Button
              size="sm"
              variant="outline"
              onClick={onGoToNumbers}
              data-testid="button-configure-phone"
            >
              Configure
            </Button>
          </div>
        )}
      </ThinCard>

      {/* Call routing */}
      <SectionLabel>Call routing</SectionLabel>
      <ThinCard>
        {ivrLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              {ROUTING_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setRoutingMode(opt.value)}
                  className={`w-full flex items-start gap-3 px-4 py-3 rounded-md border text-left transition-colors ${
                    routingMode === opt.value
                      ? "border-primary bg-primary/5"
                      : "border-border/60 hover:border-border"
                  }`}
                  data-testid={`button-routing-${opt.value}`}
                >
                  <div className={`w-3.5 h-3.5 rounded-full border-2 mt-0.5 flex-shrink-0 transition-colors ${
                    routingMode === opt.value ? "border-primary bg-primary" : "border-muted-foreground/40"
                  }`} />
                  <div>
                    <div className="text-sm font-medium">{opt.label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{opt.desc}</div>
                  </div>
                </button>
              ))}
            </div>

            {/* Keypress menu items */}
            {routingMode === "menu" && (
              <div className="space-y-2 pt-1">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Menu options</Label>
                  <Button size="sm" variant="outline" onClick={addMenuItem} disabled={menuItems.length >= 9} className="h-7 text-xs gap-1" data-testid="button-ivr-add-menu">
                    <Plus className="h-3 w-3" /> Add option
                  </Button>
                </div>
                {menuItems.length === 0 && <p className="text-xs text-muted-foreground">No menu options yet. Add one above.</p>}
                {menuItems.map((item, idx) => {
                  const patch = (fields: Partial<MenuItemDraft>) =>
                    setMenuItems((p) => p.map((x, i) => (i === idx ? { ...x, ...fields } : x)));
                  const groupUsers = item.userIds.map((id) => users.find((u) => u.userId === id)).filter(Boolean) as DialerUser[];
                  return (
                  <div key={idx} className="space-y-1.5 rounded-md border border-border/60 p-2" data-testid={`row-ivr-menu-${idx}`}>
                    <div className="flex items-center gap-2">
                    <Select value={item.key} onValueChange={(v) => patch({ key: v })}>
                      <SelectTrigger className="h-7 w-16 text-xs" data-testid={`select-menu-key-${idx}`}><SelectValue placeholder="#" /></SelectTrigger>
                      <SelectContent>{["1","2","3","4","5","6","7","8","9"].map((k) => <SelectItem key={k} value={k}>Press {k}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input value={item.label} onChange={(e) => patch({ label: e.target.value })} placeholder="Label (e.g. Sales)" className="h-7 text-xs flex-1" data-testid={`input-menu-label-${idx}`} />
                    <Select value={item.action} onValueChange={(v) => patch({ action: v })}>
                      <SelectTrigger className="h-7 w-36 text-xs" data-testid={`select-menu-action-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MENU_ACTION_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {item.action === "ring_user" && (
                      <>
                        <Select value={item.userId} onValueChange={(v) => patch({ userId: v })}>
                          <SelectTrigger className="h-7 w-36 text-xs" data-testid={`select-menu-user-${idx}`}><SelectValue placeholder="Pick rep" /></SelectTrigger>
                          <SelectContent>{users.map((u) => <SelectItem key={u.userId} value={u.userId}>{u.name || u.email}</SelectItem>)}</SelectContent>
                        </Select>
                        {/* Routing reality for this menu option (Task #612). */}
                        <PhoneRoutingBadge
                          user={users.find((u) => u.userId === item.userId)}
                          testId={`badge-menu-routing-${idx}`}
                        />
                      </>
                    )}
                    {item.action === "ring_group" && (
                      <GroupRepPicker
                        users={users}
                        selected={item.userIds}
                        onChange={(next) => patch({ userIds: next })}
                        testId={`picker-menu-group-${idx}`}
                      />
                    )}
                    {item.action === "external_forward" && (
                      <Input
                        value={item.forwardNumber}
                        onChange={(e) => patch({ forwardNumber: e.target.value })}
                        placeholder="+15551234567"
                        className="h-7 w-40 text-xs font-mono"
                        data-testid={`input-menu-forward-${idx}`}
                      />
                    )}
                    <button type="button" onClick={() => setMenuItems((p) => p.filter((_, i) => i !== idx))} className="h-7 w-7 flex items-center justify-center rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors" data-testid={`button-menu-remove-${idx}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                    </div>
                    {/* Routing reality for every member of the group (Task #613) —
                        same warning the single-rep picker shows, one per member. */}
                    {item.action === "ring_group" && (
                      <div className="flex flex-wrap items-center gap-1.5 pl-[4.75rem]" data-testid={`panel-menu-group-routing-${idx}`}>
                        {groupUsers.length === 0 ? (
                          <span className="text-[11px] text-amber-600 dark:text-amber-400 inline-flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" /> This option rings nobody. Pick at least one rep.
                          </span>
                        ) : groupUsers.map((u) => (
                          <span key={u.userId} className="inline-flex items-center gap-1" data-testid={`chip-menu-group-${idx}-${u.userId}`}>
                            <span className="text-[11px] text-muted-foreground">{u.name || u.email}</span>
                            <PhoneRoutingBadge user={u} testId={`badge-menu-group-routing-${idx}-${u.userId}`} />
                          </span>
                        ))}
                      </div>
                    )}
                    {item.action === "external_forward" && item.forwardNumber.trim() !== "" && !/^\+?[1-9][\d\s().-]{6,25}$/.test(item.forwardNumber.trim()) && (
                      <p className="text-[11px] text-amber-600 dark:text-amber-400 pl-[4.75rem]" data-testid={`text-menu-forward-invalid-${idx}`}>
                        Enter an E.164 number, e.g. +15551234567.
                      </p>
                    )}
                  </div>
                  );
                })}
              </div>
            )}

            {/* Ring group reality (Task #612) — for every mode that rings the
                whole team, show which reps will actually be reached when no
                browser tab is open. */}
            {routingMode !== "menu" && (
              <div className="space-y-1.5 pt-1" data-testid="panel-ring-group-routing">
                <Label className="text-xs">Who this rings</Label>
                {users.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No reps in this workspace yet.</p>
                ) : (
                  <div className="rounded-md border border-border/60 divide-y divide-border/40">
                    {users.map((u) => (
                      <div key={u.userId} className="flex items-center gap-2 px-2.5 py-1.5" data-testid={`row-ring-target-${u.userId}`}>
                        <span className="text-xs truncate flex-1">{u.name || u.email}</span>
                        <PhoneRoutingBadge user={u} testId={`badge-ring-routing-${u.userId}`} />
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  A rep's saved personal phone always rings on inbound calls, at the same time as their browser.
                  Reps with no number on file only ring when they have Clarik open in a tab.
                </p>
              </div>
            )}

            <div className="h-px bg-border/40" />

            {/* Greeting */}
            <div className="space-y-1.5">
              <Label htmlFor="cfg-greeting" className="text-xs">Greeting (optional)</Label>
              <Textarea id="cfg-greeting" value={greetingText} onChange={(e) => setGreetingText(e.target.value)} placeholder="Thank you for calling. Please hold while we connect you." rows={2} className="text-sm resize-none" data-testid="textarea-ivr-greeting" />
              <p className="text-xs text-muted-foreground">Played via text-to-speech before routing. Leave blank to skip.</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="cfg-timeout" className="text-xs">Ring timeout (seconds)</Label>
                <Input id="cfg-timeout" type="number" min={10} max={120} value={ringTimeout} onChange={(e) => setRingTimeout(e.target.value)} className="h-8 text-sm" data-testid="input-ivr-timeout" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">If no answer</Label>
                <Select value={missedCallAction} onValueChange={setMissedCallAction}>
                  <SelectTrigger className="h-8 text-sm" data-testid="select-ivr-missed"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="voicemail">Record voicemail</SelectItem><SelectItem value="hangup">Hang up</SelectItem></SelectContent>
                </Select>
              </div>
            </div>

            {/* Workspace fallback forwarding number (Task #612) */}
            <div className="space-y-1.5">
              <Label htmlFor="cfg-fallback-forward" className="text-xs">Fallback forwarding number (optional)</Label>
              <Input
                id="cfg-fallback-forward"
                value={fallbackForward}
                onChange={(e) => setFallbackForward(e.target.value)}
                placeholder="+15551234567"
                className="h-8 text-sm font-mono"
                data-testid="input-ivr-fallback-forward"
              />
              <p className="text-xs text-muted-foreground">
                If nobody in the ring group picks up — browser or personal phone — the call is forwarded
                here before {missedCallAction === "voicemail" ? "voicemail" : "hanging up"}. Leave blank to skip
                straight to {missedCallAction === "voicemail" ? "voicemail" : "hangup"}. E.164 format.
              </p>
            </div>

            {missedCallAction === "voicemail" && (
              <div className="space-y-3">
                {/* Recorded audio greeting */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Voicemail greeting — audio</Label>
                  {!isRecording && !recordedUrl && (
                    <div className="flex items-center gap-2">
                      {hasAudioGreeting ? (
                        <>
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground flex-1">
                            <Voicemail className="h-3.5 w-3.5 text-green-500" />
                            <span>Custom audio greeting saved</span>
                          </div>
                          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={startRecording} data-testid="button-rerecord-greeting">
                            <Mic className="h-3 w-3" />Re-record
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-destructive hover:text-destructive" onClick={() => deleteAudioGreeting.mutate()} disabled={deleteAudioGreeting.isPending} data-testid="button-delete-greeting">
                            {deleteAudioGreeting.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={startRecording} data-testid="button-start-recording">
                          <Mic className="h-3 w-3" />Record greeting
                        </Button>
                      )}
                    </div>
                  )}
                  {isRecording && (
                    <div className="flex items-center gap-2 rounded-md border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 px-3 py-2">
                      <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                      <span className="text-xs font-medium text-red-700 dark:text-red-400 flex-1">
                        Recording… {Math.floor(recordingSeconds / 60)}:{String(recordingSeconds % 60).padStart(2, "0")}
                      </span>
                      <Button size="sm" variant="destructive" className="h-7 text-xs gap-1" onClick={stopRecording} data-testid="button-stop-recording">
                        <Square className="h-3 w-3 fill-current" />Stop
                      </Button>
                    </div>
                  )}
                  {!isRecording && recordedUrl && recordedBlob && (
                    <div className="space-y-2 rounded-md border border-border/60 p-2">
                      <audio controls src={recordedUrl} className="w-full h-8" data-testid="audio-preview-greeting" />
                      <div className="flex items-center gap-2">
                        <Button size="sm" className="h-7 text-xs gap-1" onClick={() => uploadAudioGreeting.mutate(recordedBlob)} disabled={uploadAudioGreeting.isPending} data-testid="button-save-greeting">
                          {uploadAudioGreeting.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                          Save greeting
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => { setRecordedBlob(null); setRecordedUrl(null); }} data-testid="button-discard-recording">
                          <X className="h-3 w-3" />Discard
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                {/* TTS fallback */}
                <div className="space-y-1.5">
                  <Label htmlFor="cfg-vm" className="text-xs text-muted-foreground">
                    {hasAudioGreeting ? "TTS fallback (not used while audio greeting is set)" : "Voicemail greeting — text-to-speech (optional)"}
                  </Label>
                  <Textarea id="cfg-vm" value={voicemailGreeting} onChange={(e) => setVoicemailGreeting(e.target.value)} placeholder="We're unavailable. Please leave a message and we'll call you back." rows={2} className={`text-sm resize-none ${hasAudioGreeting ? "opacity-50" : ""}`} data-testid="textarea-ivr-voicemail" />
                </div>
              </div>
            )}

            <Button onClick={() => saveIvr.mutate()} disabled={saveIvr.isPending} className="h-8 text-sm gap-1.5" data-testid="button-ivr-save">
              {saveIvr.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              Save configuration
            </Button>
          </div>
        )}
      </ThinCard>

      {/* Recording */}
      <div className="flex items-center justify-between">
        <SectionLabel>Call recording</SectionLabel>
        <Button onClick={() => saveRecording.mutate()} disabled={saveRecording.isPending} size="sm" className="h-8 text-xs gap-1.5" data-testid="button-recording-save-header">
          {saveRecording.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          Save
        </Button>
      </div>
      <ThinCard>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cfg-rec-mode" className="text-xs">Default recording mode</Label>
            <select id="cfg-rec-mode" value={recordingMode} onChange={(e) => setRecordingMode(e.target.value)} className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm" data-testid="select-recording-mode">
              {Object.entries(RECORDING_MODE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 p-2 mt-1">
              <Info className="h-3.5 w-3.5 mt-0.5 text-amber-700 dark:text-amber-400 flex-shrink-0" />
              <p className="text-xs text-amber-900 dark:text-amber-200">11 U.S. states require two-party consent before recording. Review your Knowledge Base for state-by-state rules.</p>
            </div>
          </div>
          {/* Compliance warning: broadly-enabled recording without smart protection */}
          {(recordingMode === "all" || recordingMode === "opt_out") && !smartRecording && (
            <div className="rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 p-2 space-y-2" data-testid="alert-recording-compliance">
              <div className="flex items-start gap-2">
                <Info className="h-3.5 w-3.5 mt-0.5 text-amber-700 dark:text-amber-400 flex-shrink-0" />
                <p className="text-xs text-amber-900 dark:text-amber-200">
                  <strong>Compliance risk:</strong> Recording is broadly enabled but Smart Recording is off. Calls to callers in two-party-consent states (CA, CT, FL, IL, MD, MA, MT, NV, NH, PA, WA) are being recorded without a jurisdictional guard.
                </p>
              </div>
              <div className="flex items-center gap-2 pl-5">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs gap-1.5 border-amber-400 text-amber-800 dark:border-amber-600 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                  onClick={() => setSmartRecording(true)}
                  data-testid="button-enable-smart-recording-shortcut"
                >
                  <Check className="h-3 w-3" />
                  Enable Smart Recording now
                </Button>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between rounded-md border border-border/60 p-3">
            <div>
              <div className="text-sm font-medium">Smart recording</div>
              <div className="text-xs text-muted-foreground">Automatically skip recording in two-party-consent states (CA, CT, FL, IL, MD, MA, MT, NV, NH, PA, WA). Applies on top of the selected mode above.</div>
            </div>
            <Switch checked={smartRecording} onCheckedChange={setSmartRecording} data-testid="switch-smart-recording" />
          </div>
          <div className="flex items-center justify-between rounded-md border border-border/60 p-3">
            <div>
              <div className="text-sm font-medium">Force for all reps</div>
              <div className="text-xs text-muted-foreground">Prevent reps from changing their own recording preference.</div>
            </div>
            <Switch checked={recordingLocked} onCheckedChange={setRecordingLocked} data-testid="switch-recording-locked" />
          </div>
          <div className="border-t border-border/50 pt-4 space-y-3">
            <div>
              <div className="text-sm font-medium">Recover past recordings from Twilio</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Recordings already stored in Twilio can be pulled into the CRM. Optionally filter by date range.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 space-y-1">
                <Label className="text-xs text-muted-foreground">From date</Label>
                <Input type="date" value={backfillDateFrom} onChange={(e) => setBackfillDateFrom(e.target.value)} className="h-8 text-sm" data-testid="input-backfill-date-from" />
              </div>
              <div className="flex-1 space-y-1">
                <Label className="text-xs text-muted-foreground">To date</Label>
                <Input type="date" value={backfillDateTo} onChange={(e) => setBackfillDateTo(e.target.value)} className="h-8 text-sm" data-testid="input-backfill-date-to" />
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => { setBackfillResult(null); backfillRecordings.mutate(); }}
              disabled={backfillRecordings.isPending}
              className="h-8 text-sm gap-1.5"
              data-testid="button-backfill-recordings"
            >
              {backfillRecordings.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              {backfillRecordings.isPending ? "Scanning Twilio…" : "Import recordings from Twilio"}
            </Button>
            {backfillResult && (
              <div className="rounded-md bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-900 p-2.5 text-xs text-green-900 dark:text-green-200" data-testid="backfill-result">
                <strong>{backfillResult.updated}</strong> recording{backfillResult.updated !== 1 ? "s" : ""} imported &mdash; {backfillResult.matched} call{backfillResult.matched !== 1 ? "s" : ""} matched out of {backfillResult.total} Twilio recordings scanned.
              </div>
            )}
          </div>
        </div>
      </ThinCard>

      {/* Per-rep phone lines */}
      <SectionLabel>Team phone lines</SectionLabel>
      <ThinCard className="p-0">
        <div className="divide-y divide-border/50">
          {users.length === 0 && (
            <div className="px-5 py-8 text-sm text-muted-foreground text-center">No users found.</div>
          )}
          {users.map((u) => <RepRow key={u.userId} user={u} onAddLine={onGoToNumbers} />)}
        </div>
      </ThinCard>
    </div>
  );
}

function RepRow({ user, onAddLine }: { user: DialerUser; onAddLine: () => void }) {
  const { toast } = useToast();
  const [locked, setLocked] = useState(user.callerIdLocked);
  const [phone, setPhone] = useState(user.bridgePhone || "");

  // Re-sync local state if the row's underlying data changes (e.g. the rep
  // updated their own number from personal settings and the list refetched).
  useEffect(() => {
    setLocked(user.callerIdLocked);
    setPhone(user.bridgePhone || "");
  }, [user.callerIdLocked, user.bridgePhone]);

  const normalizedPhone = phone.trim();
  const dirty =
    locked !== user.callerIdLocked || normalizedPhone !== (user.bridgePhone || "");

  const save = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PATCH", `/api/admin/dialer/users/${user.userId}`, {
        callerIdLocked: locked,
        bridgePhone: normalizedPhone === "" ? null : normalizedPhone,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Saved" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/dialer/users"] });
    },
    onError: (e: any) => toast({ title: "Save failed", description: String(e?.message || e), variant: "destructive" }),
  });

  return (
    <div className="px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`row-rep-configure-${user.userId}`}>
      <div className="flex items-center gap-2 w-44 flex-shrink-0">
        <Initials name={user.name || user.email || "?"} />
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{user.name}</div>
          <div className="text-[11px] text-muted-foreground truncate">{user.email}</div>
        </div>
      </div>
      {/* Personal phone — editable by admin or by the rep in their Profile (syncs both ways). */}
      <div className="flex items-center gap-1.5 flex-1 min-w-0">
        <Input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="No personal phone set"
          title="Personal phone in E.164 format (e.g. +15551234567). The rep can also edit this from their own settings."
          className="h-8 text-xs font-mono"
          data-testid={`input-phone-${user.userId}`}
        />
        <span className="text-[10px] text-muted-foreground/60 flex-shrink-0">personal</span>
      </div>
      {/* Lock toggle — forces browser-only, prevents rep switching to "Dial to my phone" */}
      <button
        type="button"
        onClick={() => setLocked(!locked)}
        className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-muted transition-colors flex-shrink-0"
        title={locked ? "Browser-only forced (unlock to allow phone routing)" : "Allow rep to choose routing mode"}
        data-testid={`button-lock-${user.userId}`}
      >
        {locked ? <Lock className="h-3.5 w-3.5 text-primary" /> : <Unlock className="h-3.5 w-3.5 text-muted-foreground" />}
      </button>
      {dirty && (
        <Button size="sm" variant="outline" className="h-8 text-xs px-3" onClick={() => save.mutate()} disabled={save.isPending} data-testid={`button-rep-save-${user.userId}`}>
          {save.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
        </Button>
      )}
      <Button size="sm" variant="ghost" className="h-8 text-xs px-3 text-primary hover:text-primary flex-shrink-0" onClick={onAddLine} data-testid={`button-addline-${user.userId}`}>
        Add a line →
      </Button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// NUMBERS DIALOG — 3-step wizard (opened by "Configure" button)
// ══════════════════════════════════════════════════════════════════════════

interface AvailableNumber {
  phoneNumber: string;
  friendlyName: string;
  locality: string;
  region: string;
}

function NumbersDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { toast } = useToast();
  const { data: overview } = useQuery<OverviewData>({ queryKey: ["/api/admin/dialer/overview"] });
  const { data: users = [] } = useQuery<DialerUser[]>({ queryKey: ["/api/admin/dialer/users"] });

  const { data: plansData } = useQuery<{ plans: Record<string, PlanConfig> }>({ queryKey: ["/api/admin/dialer/plans"] });
  const currentTier = overview?.tier || "lite";
  const currentPlan = mergePlan(currentTier, plansData?.plans);

  const [areaCode, setAreaCode] = useState("");
  const [selectedNumber, setSelectedNumber] = useState<string | null>(null);
  const [assignTo, setAssignTo] = useState("__workspace__");
  const [searchTrigger, setSearchTrigger] = useState(0);

  const { data: numbersData, isLoading: numbersLoading, isError: numbersError } = useQuery<{ numbers: AvailableNumber[] }>({
    queryKey: ["/api/admin/dialer/available-numbers", areaCode, searchTrigger],
    queryFn: async () => {
      const url = areaCode.length === 3
        ? `/api/admin/dialer/available-numbers?areaCode=${areaCode}`
        : `/api/admin/dialer/available-numbers`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load numbers");
      return res.json();
    },
    enabled: true,
    staleTime: 30_000,
  });

  const numbers = numbersData?.numbers || [];

  const planCards: Array<{ tier: string; plan: MergedPlan }> = [
    { tier: "lite", plan: mergePlan("lite", plansData?.plans) },
    { tier: "pro", plan: mergePlan("pro", plansData?.plans) },
    { tier: "enterprise", plan: mergePlan("enterprise", plansData?.plans) },
  ];

  const selectedNumberObj = numbers.find((n) => n.phoneNumber === selectedNumber);
  const isEnterprise = currentTier === "enterprise" || currentTier === "enterprise_plus";

  const activateMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/admin/dialer/numbers/provision", {
        method: "POST",
        credentials: "include",
        headers: await withCsrfHeader({ "Content-Type": "application/json" }),
        body: JSON.stringify({ phoneNumber: selectedNumber }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `Provisioning failed (${res.status})`);
      }
      return res.json() as Promise<{ ok: boolean; phoneNumber: string }>;
    },
    onSuccess: (data) => {
      const friendly = data.phoneNumber
        ? data.phoneNumber.replace(/^\+1(\d{3})(\d{3})(\d{4})$/, "($1) $2-$3")
        : selectedNumberObj?.friendlyName || "your number";
      toast({
        title: "Number activated!",
        description: `${friendly} is ready to use. A confirmation has been sent to your email.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/dialer/overview"] });
      queryClient.invalidateQueries({ queryKey: ["/api/workspace/sms-status"] });
      onOpenChange(false);
    },
    onError: (err: Error) => {
      toast({
        title: "Provisioning failed",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add a phone number</DialogTitle>
        </DialogHeader>
    <div className="space-y-5 mt-2">
      {/* Step 1: Plan */}
      <ThinCard>
        <div className="flex items-center gap-2 mb-4">
          <StepBadge n={1} done={true} />
          <div className="text-sm font-medium">Choose a plan</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {planCards.map(({ tier, plan }) => {
            const isCurrentTier = currentTier === tier || (tier === "enterprise" && (currentTier === "enterprise" || currentTier === "enterprise_plus"));
            const isLite = tier === "lite";
            const isPro = tier === "pro" || tier === "business";
            return (
              <div
                key={tier}
                className={`rounded-lg border p-4 ${
                  isCurrentTier
                    ? "border-primary border-2"
                    : isLite ? "border-border/60" : isPro ? "border-border/60" : "border-blue-200 dark:border-blue-800"
                }`}
              >
                {isCurrentTier && (
                  <div className="text-[10px] font-semibold text-primary mb-2 uppercase tracking-wide">Your plan</div>
                )}
                {tier === "pro" && !isCurrentTier && (
                  <div className="text-[10px] font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Most popular</div>
                )}
                <div className="text-sm font-semibold mb-1">{plan.label}</div>
                <div className="text-2xl font-semibold font-mono mb-0.5">
                  {plan.mainCents === 0 ? (
                    <><span className="text-base">Included</span></>
                  ) : (
                    <>${plan.mainCents / 100}<span className="text-xs font-normal text-muted-foreground font-sans">/mo per line</span></>
                  )}
                </div>
                {plan.userCents > 0 && plan.mainCents > 0 && (
                  <div className="text-xs text-muted-foreground mb-3">+${plan.userCents / 100}/mo additional lines</div>
                )}
                {plan.mainCents === 0 && (
                  <div className="text-xs text-muted-foreground mb-3">${plan.userCents / 100}/mo additional lines</div>
                )}
                <div className="space-y-1.5">
                  {[
                    `${plan.mins.toLocaleString()} min/mo`,
                    `${plan.sms.toLocaleString()} SMS/mo`,
                    ...plan.features,
                  ].map((f) => (
                    <div key={f} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                      <Check className="w-3 h-3 text-primary flex-shrink-0 mt-0.5" />
                      {f}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 text-xs text-muted-foreground">
          Overage: ${(currentPlan.overageMinCents / 100).toFixed(2)}/min · ${(currentPlan.overageSmsCents / 100).toFixed(2)}/SMS after included pool.
        </div>
      </ThinCard>

      {/* Step 2: Pick number */}
      <ThinCard>
        <div className="flex items-center gap-2 mb-4">
          <StepBadge n={2} />
          <div className="text-sm font-medium">Pick your number</div>
        </div>

        <div className="flex gap-2 mb-3">
          <Input
            value={areaCode}
            onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, "").slice(0, 3))}
            placeholder="Area code (e.g. 612)"
            className="h-8 text-sm w-44"
            maxLength={3}
            data-testid="input-area-code"
          />
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1.5"
            onClick={() => setSearchTrigger((n) => n + 1)}
            disabled={numbersLoading}
            data-testid="button-search-numbers"
          >
            {numbersLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {areaCode.length === 3 ? `Search ${areaCode}` : "Show available"}
          </Button>
        </div>

        {numbersError && (
          <p className="text-xs text-destructive mb-3">Failed to load available numbers. Please try again.</p>
        )}

        {numbers.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {numbers.map((n) => (
              <button
                key={n.phoneNumber}
                type="button"
                onClick={() => setSelectedNumber(n.phoneNumber)}
                className={`rounded-md border p-3 text-left transition-colors ${
                  selectedNumber === n.phoneNumber
                    ? "border-primary bg-primary/5"
                    : "border-border/60 hover:border-border"
                }`}
                data-testid={`button-number-${n.phoneNumber}`}
              >
                <div className="text-xs font-semibold font-mono">{n.friendlyName}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{n.locality}{n.locality && n.region ? ", " : ""}{n.region}</div>
              </button>
            ))}
          </div>
        )}

        {numbers.length === 0 && !numbersLoading && !numbersError && (
          <p className="text-xs text-muted-foreground">Enter an area code and click Search to see available numbers.</p>
        )}

        {numbers.length > 0 && (
          <div className="mt-3 flex items-center gap-3">
            <Label className="text-xs flex-shrink-0">Assign to rep (optional)</Label>
            <Select value={assignTo} onValueChange={setAssignTo}>
              <SelectTrigger className="h-8 text-xs w-52" data-testid="select-assign-rep"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__workspace__">— workspace number —</SelectItem>
                {users.map((u) => <SelectItem key={u.userId} value={u.userId}>{u.name || u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </ThinCard>

      {/* Step 3: Activate */}
      <ThinCard>
        <div className="flex items-center gap-2 mb-4">
          <StepBadge n={3} done={!!selectedNumber} />
          <div className="text-sm font-medium">Activate</div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            className="flex-1 h-10 gap-2 text-sm"
            disabled={!selectedNumber || activateMutation.isPending}
            onClick={() => activateMutation.mutate()}
            data-testid="button-activate-number"
          >
            {activateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Phone className="h-4 w-4" />
            )}
            {selectedNumber
              ? `Activate ${selectedNumberObj?.friendlyName || selectedNumber}${
                  isEnterprise && assignTo === "__workspace__" && !overview?.phoneProvisioned
                    ? " — Included"
                    : ` — ${centsToDisplay(currentPlan.mainCents)}`
                }`
              : "Select a number above"}
          </Button>
          <Button variant="outline" className="h-10 gap-2 text-sm" data-testid="button-port-number">
            <ChevronRight className="h-4 w-4" />
            Port existing number
          </Button>
        </div>

        {selectedNumber && (
          <p className="text-xs text-muted-foreground mt-3">
            {isEnterprise
              ? "Enterprise plan includes your first number free. Additional lines billed at $10/mo."
              : `Charged ${centsToDisplay(currentPlan.mainCents)} immediately, then monthly. Includes ${currentPlan.mins} min and ${currentPlan.sms} SMS pooled per billing period.`}
          </p>
        )}
      </ThinCard>
    </div>
      </DialogContent>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// USAGE TAB
// ══════════════════════════════════════════════════════════════════════════

function UsageTab() {
  const { data: phoneData } = useQuery<PhoneStatusData>({ queryKey: ["/api/admin/phone"] });
  const { data: overview } = useQuery<OverviewData>({ queryKey: ["/api/admin/dialer/overview"] });
  const { data: plansData } = useQuery<{ plans: Record<string, PlanConfig> }>({ queryKey: ["/api/admin/dialer/plans"] });
  const tier = overview?.tier || "lite";
  const plan = mergePlan(tier, plansData?.plans);

  const usage = phoneData?.usage;
  const billing = phoneData?.billing;

  const billingLabel = usage?.billingMonth
    ? (() => {
        const [y, m] = usage.billingMonth.split("-").map(Number);
        const d = new Date(y, m - 1, 1);
        const last = new Date(y, m, 0).getDate();
        return `${d.toLocaleString("default", { month: "short" })} 1–${last}`;
      })()
    : "";

  const totalMins = overview?.minutesThisMonth ?? usage?.minutesUsed ?? 0;
  const totalSms = usage?.smsUsed ?? 0;
  const includedMins = billing?.includedMinutes ?? plan.mins;
  const includedSms = billing?.includedSms ?? plan.sms;

  const overageMinCents = billing?.overagePerMinuteCents ?? plan.overageMinCents;
  const overageSmsCents = billing?.overagePerSmsCents ?? plan.overageSmsCents;
  const overageMins = Math.max(0, totalMins - includedMins);
  const overageSms = Math.max(0, totalSms - includedSms);
  const overageCents = overageMins * overageMinCents + overageSms * overageSmsCents;

  const handleExport = useCallback(() => {
    if (!overview?.reps) return;
    const rows = [
      ["Name", "Calls this month", "Minutes this month"],
      ...overview.reps.map((r) => [r.name, r.callsThisMonth, r.minutesThisMonth]),
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dialer-usage-${usage?.billingMonth || "current"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [overview, usage]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <SectionLabel>This billing period{billingLabel ? ` · ${billingLabel}` : ""}</SectionLabel>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total calls", value: (overview?.callsThisMonth ?? 0).toLocaleString() },
          { label: "Total minutes", value: totalMins.toLocaleString() },
          { label: "SMS sent", value: totalSms.toLocaleString() },
          { label: "Est. overage", value: overageCents > 0 ? `$${(overageCents / 100).toFixed(2)}` : "$0.00" },
        ].map(({ label, value }) => (
          <div key={label} className="bg-muted/40 rounded-md p-3">
            <div className="text-[11px] text-muted-foreground mb-1">{label}</div>
            <div className="text-xl font-semibold font-mono">{value}</div>
          </div>
        ))}
      </div>

      {/* Included usage bars */}
      <ThinCard>
        <div className="space-y-3">
          <UsageBar label="Minutes" used={totalMins} included={includedMins} unit="min" />
          <UsageBar label="SMS" used={totalSms} included={includedSms} unit="msg" />
        </div>
        {overageCents > 0 && (
          <div className="mt-4 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 p-3 text-xs text-amber-900 dark:text-amber-300">
            Estimated overage this period: <span className="font-semibold">${(overageCents / 100).toFixed(2)}</span>
            {" · "}
            {overageMins > 0 && `${overageMins} extra min × $${(overageMinCents / 100).toFixed(2)}`}
            {overageMins > 0 && overageSms > 0 && " + "}
            {overageSms > 0 && `${overageSms} extra SMS × $${(overageSmsCents / 100).toFixed(2)}`}
          </div>
        )}
      </ThinCard>

      <SectionLabel>By rep</SectionLabel>
      <ThinCard className="p-0">
        <div className="divide-y divide-border/50">
          {(overview?.reps ?? []).map((rep) => (
            <div key={rep.userId} className="flex items-center gap-3 px-5 py-3" data-testid={`row-rep-usage-${rep.userId}`}>
              <Initials name={rep.name} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{rep.name}</div>
              </div>
              <div className="text-xs text-muted-foreground font-mono">
                {rep.callsThisMonth} calls · {rep.minutesThisMonth} min
              </div>
              <div className="w-24 h-1 bg-muted rounded-full overflow-hidden hidden sm:block">
                <div className="h-1 bg-primary rounded-full transition-all" style={{ width: `${rep.usagePct}%` }} />
              </div>
            </div>
          ))}
          {(!overview?.reps || overview.reps.length === 0) && (
            <div className="px-5 py-8 text-sm text-muted-foreground text-center">No usage data yet this period.</div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-border/50">
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={handleExport} data-testid="button-export-csv">
            <Download className="h-3.5 w-3.5" /> Export usage CSV
          </Button>
        </div>
      </ThinCard>
    </div>
  );
}

function UsageBar({ label, used, included, unit }: { label: string; used: number; included: number; unit: string }) {
  const pct = included > 0 ? Math.min(100, Math.round((used / included) * 100)) : 0;
  const over = used > included;
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className={`text-xs font-mono ${over ? "text-amber-600 dark:text-amber-400" : "text-foreground"}`}>
          {used.toLocaleString()} / {included.toLocaleString()} {unit}
        </span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-1.5 rounded-full transition-all ${over ? "bg-amber-500" : "bg-primary"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// CALLS TAB — workspace-scoped log of real placed/received calls
// ══════════════════════════════════════════════════════════════════════════

// ── Tenant Dialer Calls (Task #575) ───────────────────────────────────────
// Real placed/logged calls from the `calls` table — NOT the AI sales-training
// scoring pipeline (that lives in the platform view). Shapes mirror the
// /api/admin/dialer/calls + /stats endpoints.

interface CallsStats {
  total: number;
  outbound: number;
  inbound: number;
  connected: number;
  lastCallAt: string | null;
}

interface DialerCall {
  id: string;
  userId: string | null;
  repName: string | null;
  leadId: string | null;
  clientId: string | null;
  contactName: string | null;
  contactKind: "lead" | "client" | null;
  direction: "inbound" | "outbound";
  fromNumber: string | null;
  toNumber: string | null;
  status: string;
  startedAt: string | null;
  endedAt: string | null;
  durationSeconds: number;
  notes: string | null;
  createdAt: string;
}

interface CallsPage { rows: DialerCall[]; nextCursor: string | null; }

function callFmtDuration(s: number): string {
  if (!s || s <= 0) return "—";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
}

function callFmtDate(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });
}

// status → outcome label + badge variant for real telephony statuses.
const CALL_STATUS_LABELS: Record<string, string> = {
  queued: "Queued",
  ringing: "Ringing",
  "in-progress": "In progress",
  completed: "Connected",
  failed: "Failed",
  busy: "Busy",
  "no-answer": "No answer",
};
const CALL_STATUS_VARIANT: Record<string, "secondary" | "default" | "destructive" | "outline"> = {
  queued: "secondary",
  ringing: "secondary",
  "in-progress": "default",
  completed: "default",
  failed: "destructive",
  busy: "outline",
  "no-answer": "outline",
};

function callNumberDisplay(call: DialerCall): string {
  // The "other party" number from the rep's perspective.
  const n = call.direction === "outbound" ? call.toNumber : call.fromNumber;
  return n || "—";
}

function CallStatCard({ title, value, icon: Icon, description, isLoading }: {
  title: string; value: string | number; icon: React.ElementType; description?: string; isLoading?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {isLoading ? <Skeleton className="h-8 w-24" /> : <div className="text-2xl font-bold">{value}</div>}
        {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
      </CardContent>
    </Card>
  );
}

function CallDirectionBadge({ direction }: { direction: DialerCall["direction"] }) {
  const isOut = direction === "outbound";
  const Icon = isOut ? PhoneOutgoing : PhoneIncoming;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${
      isOut ? "text-blue-600 dark:text-blue-400" : "text-emerald-600 dark:text-emerald-400"
    }`}>
      <Icon className="h-3.5 w-3.5" />
      {isOut ? "Outbound" : "Inbound"}
    </span>
  );
}

function CallDetailDrawer({ call, open, onClose }: {
  call: DialerCall | null; open: boolean; onClose: () => void;
}) {
  if (!call) return null;
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="call-detail-drawer">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            <PhoneCall className="h-4 w-4" />
            Call Detail
          </SheetTitle>
        </SheetHeader>
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-y-4 gap-x-3 text-sm">
            <div>
              <span className="text-muted-foreground flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> Rep</span>
              <p className="mt-0.5" data-testid="detail-call-rep">{call.repName || "—"}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Direction</span>
              <div className="mt-0.5"><CallDirectionBadge direction={call.direction} /></div>
            </div>
            <div>
              <span className="text-muted-foreground flex items-center gap-1.5"><Clock3 className="h-3.5 w-3.5" /> Date</span>
              <p className="mt-0.5">{callFmtDate(call.createdAt)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Duration</span>
              <p className="mt-0.5 tabular-nums">{callFmtDuration(call.durationSeconds)}</p>
            </div>
            <div>
              <span className="text-muted-foreground flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> Number</span>
              <p className="mt-0.5 tabular-nums">{callNumberDisplay(call)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Status</span>
              <Badge variant={CALL_STATUS_VARIANT[call.status] ?? "secondary"} className="mt-0.5 block w-fit">
                {CALL_STATUS_LABELS[call.status] ?? call.status}
              </Badge>
            </div>
            <div className="col-span-2">
              <span className="text-muted-foreground">Linked {call.contactKind === "client" ? "client" : call.contactKind === "lead" ? "lead" : "contact"}</span>
              <p className="mt-0.5" data-testid="detail-call-contact">{call.contactName || "—"}</p>
            </div>
          </div>
          {call.notes && (
            <>
              <Separator />
              <div>
                <span className="text-muted-foreground text-sm flex items-center gap-1.5 mb-1.5"><FileText className="h-3.5 w-3.5" /> Notes</span>
                <p className="text-sm whitespace-pre-wrap leading-relaxed" data-testid="detail-call-notes">{call.notes}</p>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── Voicemail Tab ─────────────────────────────────────────────────────────────

interface VoicemailRow {
  id: string;
  workspaceId: string;
  assignedUserId: string | null;
  assignedUserName: string | null;
  fromNumber: string | null;
  twilioCallSid: string | null;
  twilioRecordingSid: string | null;
  recordingUrl: string | null;
  durationSeconds: number;
  transcription: string | null;
  listenedAt: string | null;
  createdAt: string;
}

function formatDuration(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatRelative(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(dateStr).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function VoicemailTab({ voicemails, isLoading }: { voicemails: VoicemailRow[]; isLoading: boolean }) {
  const { toast } = useToast();

  const listenedMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("PATCH", `/api/dialer/voicemails/${id}/listened`);
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/dialer/voicemails"] }); },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/dialer/voicemails/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/dialer/voicemails"] });
      toast({ title: "Voicemail deleted" });
    },
    onError: () => { toast({ title: "Failed to delete voicemail", variant: "destructive" }); },
  });

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="rounded-lg border border-border/60 p-4">
            <div className="flex items-start gap-3">
              <Skeleton className="w-2 h-2 rounded-full mt-1.5 shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-20" />
                </div>
                <Skeleton className="h-8 w-full" />
              </div>
              <Skeleton className="h-7 w-7 rounded shrink-0" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (voicemails.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Voicemail className="w-12 h-12 text-muted-foreground/40 mb-3" />
        <p className="text-sm font-medium text-muted-foreground">No voicemails yet</p>
        <p className="text-xs text-muted-foreground/60 mt-1">Missed calls that go to voicemail will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {voicemails.map((vm) => {
        const isNew = !vm.listenedAt;
        const dt = new Date(vm.createdAt);
        const absoluteLabel = `${dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} ${dt.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;
        return (
          <div
            key={vm.id}
            data-testid={`voicemail-row-${vm.id}`}
            className={`rounded-lg border p-4 transition-colors ${
              isNew
                ? "border-primary/30 bg-primary/5 dark:bg-primary/10"
                : "border-border/60 bg-card"
            }`}
          >
            <div className="flex items-start gap-3">
              {/* Unread indicator */}
              <div className="mt-1.5 shrink-0">
                {isNew ? (
                  <span className="inline-block w-2 h-2 rounded-full bg-primary" data-testid={`voicemail-unread-dot-${vm.id}`} />
                ) : (
                  <span className="inline-block w-2 h-2 rounded-full bg-transparent" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                {/* Header row */}
                <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm" data-testid={`voicemail-from-${vm.id}`}>
                      {vm.fromNumber || "Unknown caller"}
                    </span>
                    {vm.assignedUserName && (
                      <span className="text-xs text-muted-foreground">→ {vm.assignedUserName}</span>
                    )}
                    {isNew && (
                      <Badge variant="outline" className="text-[10px] h-4 px-1.5 text-primary border-primary/40">New</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span>{formatDuration(vm.durationSeconds)}</span>
                    <span>·</span>
                    <span title={absoluteLabel}>{formatRelative(vm.createdAt)}</span>
                  </div>
                </div>

                {/* Audio player */}
                {vm.recordingUrl ? (
                  <audio
                    controls
                    src={`/api/dialer/voicemails/${vm.id}/recording`}
                    className="w-full h-8 mt-2"
                    data-testid={`voicemail-audio-${vm.id}`}
                    onPlay={() => {
                      if (isNew) listenedMutation.mutate(vm.id);
                    }}
                  />
                ) : (
                  <p className="text-xs text-muted-foreground/60 mt-1 italic">Recording not available</p>
                )}

                {/* Transcription (when present) */}
                {vm.transcription && (
                  <p className="text-xs text-muted-foreground mt-2 border-t border-border/40 pt-2">{vm.transcription}</p>
                )}
              </div>

              {/* Delete button */}
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0 h-7 w-7 text-muted-foreground hover:text-destructive"
                data-testid={`voicemail-delete-${vm.id}`}
                onClick={() => deleteMutation.mutate(vm.id)}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CallsTab() {
  const [status, setStatus] = useState("");
  const [direction, setDirection] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [cursor, setCursor] = useState<string | null>(null);
  const [cursors, setCursors] = useState<string[]>([]);
  const [selectedCall, setSelectedCall] = useState<DialerCall | null>(null);

  const { data: stats, isLoading: statsLoading } = useQuery<CallsStats>({
    queryKey: ["/api/admin/dialer/calls/stats"],
  });

  const buildParams = useCallback((cur: string | null) => {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (direction) p.set("direction", direction);
    if (dateFrom) p.set("dateFrom", new Date(dateFrom).toISOString());
    if (dateTo) { const d = new Date(dateTo); d.setHours(23, 59, 59, 999); p.set("dateTo", d.toISOString()); }
    if (cur) p.set("cursor", cur);
    return p.toString();
  }, [status, direction, dateFrom, dateTo]);

  const queryParams = buildParams(cursor);

  const { data: page, isLoading: pageLoading } = useQuery<CallsPage>({
    queryKey: ["/api/admin/dialer/calls", queryParams],
    queryFn: async () => {
      const res = await fetch(`/api/admin/dialer/calls?${queryParams}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load calls");
      return res.json();
    },
  });

  const resetPagination = () => { setCursor(null); setCursors([]); };
  const activeFilters = status || direction || dateFrom || dateTo;

  const calls = page?.rows ?? [];
  const page0 = cursors.length === 0 && !cursor;
  const lastCall = stats?.lastCallAt ? callFmtDate(stats.lastCallAt) : "—";

  return (
    <>
      <div className="space-y-5" data-testid="dialer-calls-tab">
        {/* Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <CallStatCard title="Total Calls" value={stats?.total ?? 0} icon={PhoneCall}
            description="Placed & received" isLoading={statsLoading} />
          <CallStatCard title="Outbound / Inbound" value={`${stats?.outbound ?? 0} / ${stats?.inbound ?? 0}`} icon={PhoneOutgoing}
            description="Direction split" isLoading={statsLoading} />
          <CallStatCard title="Connected" value={stats?.connected ?? 0} icon={Check}
            description="Calls that completed" isLoading={statsLoading} />
          <CallStatCard title="Most Recent" value={lastCall} icon={Clock}
            description="Last call logged" isLoading={statsLoading} />
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap gap-3 items-end">
              <Filter className="h-4 w-4 text-muted-foreground self-center mb-1 shrink-0" />
              <div className="flex flex-col gap-1 min-w-[130px]">
                <Label className="text-xs">Status</Label>
                <Select value={status || "__all__"} onValueChange={(v) => { setStatus(v === "__all__" ? "" : v); resetPagination(); }}>
                  <SelectTrigger className="h-8 text-xs" data-testid="select-call-status">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All statuses</SelectItem>
                    <SelectItem value="completed">Connected</SelectItem>
                    <SelectItem value="no-answer">No answer</SelectItem>
                    <SelectItem value="busy">Busy</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="in-progress">In progress</SelectItem>
                    <SelectItem value="ringing">Ringing</SelectItem>
                    <SelectItem value="queued">Queued</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1 min-w-[130px]">
                <Label className="text-xs">Direction</Label>
                <Select value={direction || "__all__"} onValueChange={(v) => { setDirection(v === "__all__" ? "" : v); resetPagination(); }}>
                  <SelectTrigger className="h-8 text-xs" data-testid="select-call-direction">
                    <SelectValue placeholder="All directions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All directions</SelectItem>
                    <SelectItem value="outbound">Outbound</SelectItem>
                    <SelectItem value="inbound">Inbound</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs">From</Label>
                <Input type="date" className="h-8 text-xs w-36" value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); resetPagination(); }}
                  data-testid="input-call-date-from" />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs">To</Label>
                <Input type="date" className="h-8 text-xs w-36" value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); resetPagination(); }}
                  data-testid="input-call-date-to" />
              </div>
              {activeFilters && (
                <Button size="sm" variant="ghost" className="h-8 text-xs gap-1 text-muted-foreground self-end"
                  onClick={() => { setStatus(""); setDirection(""); setDateFrom(""); setDateTo(""); resetPagination(); }}
                  data-testid="button-clear-call-filters">
                  <X className="h-3 w-3" /> Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="dialer-calls-table">
              <thead>
                <tr className="border-b bg-muted/30 text-xs text-muted-foreground uppercase tracking-wide">
                  <th className="px-4 py-3 text-left font-medium">Rep</th>
                  <th className="px-4 py-3 text-left font-medium">Date</th>
                  <th className="px-4 py-3 text-left font-medium">Direction</th>
                  <th className="px-4 py-3 text-left font-medium">Number</th>
                  <th className="px-4 py-3 text-left font-medium">Contact</th>
                  <th className="px-4 py-3 text-left font-medium">Duration</th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                  <th className="px-4 py-3 text-left font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {pageLoading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i} className="border-b">
                      {Array.from({ length: 8 }).map((__, j) => (
                        <td key={j} className="px-4 py-3"><Skeleton className="h-4 w-full" /></td>
                      ))}
                    </tr>
                  ))
                ) : calls.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-muted-foreground text-sm">
                      No calls found. Placed and received calls will appear here once your team starts dialing.
                    </td>
                  </tr>
                ) : (
                  calls.map((call) => (
                    <tr key={call.id} className="border-b hover:bg-muted/40 cursor-pointer transition-colors"
                      onClick={() => setSelectedCall(call)} data-testid={`row-call-${call.id}`}>
                      <td className="px-4 py-3 text-xs whitespace-nowrap" data-testid={`text-call-rep-${call.id}`}>
                        {call.repName || <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-4 py-3 text-xs whitespace-nowrap">{callFmtDate(call.createdAt)}</td>
                      <td className="px-4 py-3 whitespace-nowrap"><CallDirectionBadge direction={call.direction} /></td>
                      <td className="px-4 py-3 text-xs tabular-nums whitespace-nowrap">{callNumberDisplay(call)}</td>
                      <td className="px-4 py-3 text-xs">
                        {call.contactName ? (
                          <span className="inline-flex items-center gap-1.5">
                            {call.contactName}
                            {call.contactKind && (
                              <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 capitalize">{call.contactKind}</Badge>
                            )}
                          </span>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-4 py-3 text-xs tabular-nums whitespace-nowrap">{callFmtDuration(call.durationSeconds)}</td>
                      <td className="px-4 py-3">
                        <Badge variant={CALL_STATUS_VARIANT[call.status] ?? "secondary"} className="text-xs">
                          {CALL_STATUS_LABELS[call.status] ?? call.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                          onClick={(e) => { e.stopPropagation(); setSelectedCall(call); }}
                          data-testid={`button-open-${call.id}`}>
                          <ChevronRight className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {(calls.length > 0 || !page0) && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <span className="text-xs text-muted-foreground">
                {calls.length > 0 ? `${calls.length} result${calls.length === 1 ? "" : "s"} on this page` : ""}
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page0}
                  onClick={() => { const prev = cursors[cursors.length - 1]; setCursors((c) => c.slice(0, -1)); setCursor(prev || null); }}
                  data-testid="button-calls-prev">Previous</Button>
                <Button size="sm" variant="outline" disabled={!page?.nextCursor}
                  onClick={() => { setCursors((c) => [...c, cursor ?? ""]); setCursor(page!.nextCursor); }}
                  data-testid="button-calls-next">Next</Button>
              </div>
            </div>
          )}
        </Card>

        <p className="text-xs text-muted-foreground">
          Every call placed from the browser dialer or logged manually appears here, linked to the rep who made it and the lead or client on the other end.
        </p>
      </div>

      <CallDetailDrawer
        call={selectedCall}
        open={!!selectedCall}
        onClose={() => setSelectedCall(null)}
      />
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// ROOT EXPORT
// ══════════════════════════════════════════════════════════════════════════

export function AdminDialerSettingsTab() {
  const [tab, setTab] = useState<Tab>("overview");
  const [numbersDialogOpen, setNumbersDialogOpen] = useState(false);
  const { data: overview } = useQuery<OverviewData>({ queryKey: ["/api/admin/dialer/overview"] });
  const tier = overview?.tier || "lite";
  const plan = PLAN_PRESENTATION[tier] || PLAN_PRESENTATION.lite;

  const { data: voicemails = [], isLoading: voicemailsLoading } = useQuery<VoicemailRow[]>({ queryKey: ["/api/dialer/voicemails"] });
  const unreadCount = voicemails.filter((v) => !v.listenedAt).length;

  const tabs: Array<{ id: Tab; label: React.ReactNode }> = [
    { id: "overview", label: "Overview" },
    {
      id: "voicemail",
      label: (
        <span className="flex items-center gap-1.5">
          Voicemail
          {unreadCount > 0 && (
            <span className="inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-semibold leading-none w-4 h-4">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </span>
      ),
    },
    { id: "configure", label: "Configure" },
    { id: "usage", label: "Usage" },
    { id: "calls", label: "Calls" },
  ];

  return (
    <div>
      {/* Page header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <h2 className="text-xl font-semibold">Dialer</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Phone numbers, call routing, and usage</p>
        </div>
        <Badge className={`text-[11px] gap-1 ${
          tier === "enterprise" || tier === "enterprise_plus"
            ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800"
            : "bg-muted text-muted-foreground border border-border/60"
        }`}>
          <PhoneCall className="w-3 h-3" />
          {plan.label}
        </Badge>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-border/60 mb-5 -mx-0">
        {tabs.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === id
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`tab-dialer-${id}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab onGoToNumbers={() => setNumbersDialogOpen(true)} />}
      {tab === "voicemail" && <VoicemailTab voicemails={voicemails} isLoading={voicemailsLoading} />}
      {tab === "configure" && <ConfigureTab onGoToNumbers={() => setNumbersDialogOpen(true)} />}
      {tab === "usage" && <UsageTab />}
      {tab === "calls" && <CallsTab />}

      <NumbersDialog open={numbersDialogOpen} onOpenChange={setNumbersDialogOpen} />
    </div>
  );
}
